import React, { useState, useEffect, useCallback, ChangeEvent, InputHTMLAttributes } from "react";

// Define the props for ManagedInput, extending standard input attributes
// Omit 'value', 'onChange', and 'checked' as they are handled internally or via specific props
interface ManagedInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "checked"> {
    /** Unique identifier for the underlying HTML input element (useful for labels) */
    id: string;
    /** The current committed value from the parent's state management hook (e.g., useDirtyState) */
    value: string | number | boolean | readonly string[]; // Allow for various input value types
    /** The function from the parent's state hook to update the latest value (e.g., the 'update' function) */
    updateValue: (newValueOrFn: React.SetStateAction<any>) => void; // Use 'any' for flexibility or create a more specific generic if needed
    /** The type of the input element (e.g., "text", "number", "checkbox", "radio", etc.) */
    type?: string; // Keep type flexible, handle specific cases below
}

/**
 * ManagedInput Component
 *
 * Integrates with a parent's state management hook (like useDirtyState)
 * via individual `value` and `updateValue` props. Handles common input
 * types like text, number, and checkbox.
 *
 * Assumes the state management hook handles update modes (immediate,
 * debounced, manual).
 */
const ManagedInput: React.FC<ManagedInputProps> = ({
    id,
    value: committedValue, // Rename prop locally to avoid conflict with internal state
    updateValue,          // Callback to update parent state manager
    type = "text",        // Default input type to text
    ...restProps          // Pass down other standard input props (disabled, placeholder, etc.)
}) => {

    // Internal state to manage the value displayed in the input field
    const [displayValue, setDisplayValue] = useState<any>(committedValue); // Use 'any' for flexibility, synced with committedValue

    // ---=== Event Handlers ===--- //

    const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        let newValue: string | number | boolean;

        // Determine the new value based on the input type
        if (type === "checkbox") {
            newValue = event.target.checked;
        }
        else if (type === "number") {
            // Attempt to parse as number, fallback to string if invalid
            newValue = event.target.value === "" ? "" : Number(event.target.value);
            if (isNaN(newValue as number)) {
                newValue = event.target.value; // Keep as string if parsing fails
            }
        }
        else {
            // Default to string value for text, email, password, etc.
            newValue = event.target.value;
        }

        // Update the local display state immediately for responsiveness
        setDisplayValue(newValue);
        // Propagate the change up to the state manager via the updateValue function
        updateValue(newValue);

    }, [type, updateValue]); // Dependencies: type determines how value is read, updateValue is the callback

    // ---=== Effects ===--- //

    // Sync internal display state if the committed value from the parent changes externally
    // (e.g., due to a reset, revert, or external update).
    useEffect(() => {
        // Check if the internal display value differs from the committed value from the parent.
        // This comparison should ideally use the same logic as the DirtyStateManager
        // for consistency, but a simple check is often sufficient here.
        // Using simple inequality check for performance, assuming direct comparison works for most cases.
        if (displayValue !== committedValue) {
            // If they differ, update the local display state to match the committed value.
            setDisplayValue(committedValue);
        }
        // Run this effect whenever the committedValue prop changes.
        // Exclude displayValue from dependencies to prevent loops when typing.
    }, [committedValue]);

    // ---=== Render ===--- //

    // Determine props based on input type
    const inputProps: InputHTMLAttributes<HTMLInputElement> = {
        id: id,
        type: type,
        onChange: handleChange,
        ...restProps // Spread remaining props like disabled, placeholder, className, etc.
    };

    // Handle 'checked' for checkboxes and 'value' for others
    if (type === "checkbox") {
        inputProps.checked = !!displayValue; // Ensure boolean value for checked
    }
    else {
        // Ensure value is not null/undefined for controlled input, default to empty string
        inputProps.value = displayValue ?? "";
    }

    return (
        <input {...inputProps} />
    );
};

export default ManagedInput;
